import { useState, useContext, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FaCheckCircle, FaExclamationTriangle, FaTrash } from "react-icons/fa";
import { useDoubleTap } from 'use-double-tap';

import AppContext from "../../../store/app-context";
import AuthContext from "../../../store/auth-context";
import { EnhancedOrderBox } from "../../../types/logistics";
import BarcodeDisplay from "../BarcodeDisplay/BarcodeDisplay";
import { barcodeShortLabel } from "../../../utils/barcode-format";

import './BoxList.scss';

type boxListProps = {
    boxes: EnhancedOrderBox[],
    progress: number,
    update: (item: EnhancedOrderBox | string) => void,
    manualMode?: boolean,
    onFailBox?: (box: EnhancedOrderBox) => void,
    onRemoveBox?: (box: EnhancedOrderBox) => void,
    orderType?: string
};

const BoxList = (props: boxListProps) => {
    const [location, setLocation] = useState('');
    const authCtx = useContext(AuthContext);
    const appCtx = useContext(AppContext);
    const { manualMode = false, onFailBox, onRemoveBox } = props;

    const currentLocation = useLocation();
    
    const boxes = props.boxes;

    const finished = () => {
        return props.progress === props.boxes.length;
    };

    // Separate boxes into actionable and completed for display
    const { actionableBoxes, completedBoxes } = useMemo(() => {
        const actionable: EnhancedOrderBox[] = [];
        const completed: EnhancedOrderBox[] = [];
        
        boxes.forEach((box) => {
            const isComplete = box.isCompleted;
            
            if (isComplete) {
                completed.push(box);
            } else {
                actionable.push(box);
            }
        });
        
        return { actionableBoxes: actionable, completedBoxes: completed };
    }, [boxes]);

    // Handle manual box toggle with confirmation
    const handleManualToggle = useCallback((box: EnhancedOrderBox) => {
        const label = barcodeShortLabel(box.barcode, box.id, box.barcodeComponents);
        if (!box.scanned) {
            appCtx.newDialog({
                text: `Mark box ${label} as scanned?`,
                confirm: () => {
                    box.scanned = true;
                    props.update(box);
                }
            });
        } else {
            appCtx.newDialog({
                text: `Unmark box ${label} as scanned?`,
                confirm: () => {
                    delete box.scanned;
                    props.update(box);
                }
            });
        }
    }, [appCtx, props]);

    // Handle box click in manual mode
    const handleBoxClick = useCallback((event: React.MouseEvent, box: EnhancedOrderBox) => {
        if (manualMode && !box.isCompleted) {
            event.preventDefault();
            event.stopPropagation();
            handleManualToggle(box);
        }
    }, [manualMode, handleManualToggle]);

    // Handle location click in manual mode (for incoming/store)
    const handleLocationClick = useCallback((event: React.MouseEvent) => {
        if (manualMode) {
            event.preventDefault();
            event.stopPropagation();
            const newLocation = appCtx.location === '' ? 'WH1-St-A-03-2-D' : '';
            appCtx.newDialog({
                text: newLocation ? 'Set location to WH1-St-A-03-2-D?' : 'Clear location?',
                confirm: () => {
                    appCtx.updateLocation(newLocation);
                    props.update(newLocation);
                }
            });
        }
    }, [manualMode, appCtx, props]);

    const bind = useDoubleTap((event: React.SyntheticEvent) => {
        const thingScanned = (event.target as HTMLElement).id;
        // Double-tap quick-toggle available to MASTER (admin) users without confirmation dialog
        if(authCtx.user?.role === 'MASTER'){
            if(thingScanned === 'scannedLocation'){
                let newLocation:string = appCtx.location === '' ? 'WH1-St-A-03-2-D' : '';
                appCtx.updateLocation(newLocation);
                props.update(newLocation);
            }else{
                // Match by box ID (extracted from element id like "box-123")
                const boxIdMatch = thingScanned.match(/^box-(\d+)$/);
                const boxId = boxIdMatch ? parseInt(boxIdMatch[1]) : null;
                
                boxes.forEach((boxInList) => {
                    // Match by ID if available, fallback to barcode matching
                    const isMatch = boxId ? boxInList.id === boxId : boxInList.barcode === thingScanned;
                    if(isMatch){
                        if(!boxInList.scanned) boxInList.scanned = true;
                        else delete boxInList.scanned;
                        props.update(boxInList);
                    }
                });
            }
        }
    });
    
    const orderType = props.orderType;
    const pathname = currentLocation.pathname;

    useEffect(() => {
        let locationString:string = '';

        if(pathname.indexOf('courier/order') >= 0 && orderType){
            if(orderType.indexOf('delivery') >= 0) locationString = 'the customer';
            if(orderType.indexOf('collection') >= 0) locationString = 'your vehicle';
        }

        if(pathname.indexOf('courier/warehouse/load') >= 0) locationString = 'your vehicle';

        if(pathname.indexOf('incoming/receive') >= 0) locationString = 'warehouse';
        if(pathname.indexOf('incoming/store') >= 0) locationString = 'shelf locations:';

        if(pathname.indexOf('dispatch/pick') >= 0) locationString = 'your dispatch area';
        if(pathname.indexOf('dispatch/load') >= 0) locationString = 'courier vehicle';

        setLocation(locationString);
    }, [orderType, pathname]);

    return (
        <>
            {!finished() && <div className="action-message">
                {manualMode ? (
                    <>Tap boxes to mark as <strong>scanned</strong></>
                ) : (
                    <>To <strong>{location}</strong></>
                )}
            </div>}
            {currentLocation.pathname.indexOf('incoming/store') >= 0 && (
                <section 
                    id="scannedLocation" 
                    className={'location' + (appCtx.location.length > 0 ? ' scanned' : '') + (manualMode ? ' manual-mode' : '')} 
                    onClick={(e) => {
                        // Handle manual mode click first
                        if (manualMode) {
                            handleLocationClick(e);
                        }
                        // Then call double-tap handler if exists
                        bind.onClick?.(e);
                    }}
                >
                    {appCtx.location.length > 0 ? appCtx.location : (manualMode ? 'Tap to set location' : 'Scan a location')}
                </section>
            )}
            <section className={'box-list' + (manualMode ? ' manual-mode' : '')}>
                {/* Actionable boxes - can be scanned/moved */}
                {actionableBoxes.map((box, index) => {
                    const enhancedBox = box as EnhancedOrderBox;
                    const isActionable = enhancedBox.isActionable ?? true;
                    // Show fail button for WCAC boxes (collection) or ITTC boxes (delivery) when callback is provided
                    const canFail = onFailBox && (enhancedBox.statusCode === 'WCAC' || enhancedBox.statusCode === 'ITTC') && !box.scanned && !box.isCompleted;
                    // Show remove button for removable boxes when callback is provided
                    const canRemove = onRemoveBox && (enhancedBox.statusCode === 'WCAC') && !box.scanned && !box.isCompleted;
                    const hasActions = !box.scanned && (canFail || canRemove);
                    // Use box ID for element id (consistent format for event handling)
                    const elementId = box.id ? `box-${box.id}` : box.barcode || `box-idx-${index}`;
                    return (
                        <div
                            key={box.id || box.barcode || `box-${index}`}
                            id={elementId}
                            className={
                                'box' +
                                (box.scanned ? ' scanned' : '') +
                                (isActionable ? ' actionable' : ' pending') +
                                (manualMode && !box.scanned ? ' tappable' : '') +
                                (hasActions ? ' has-actions' : '') +
                                (enhancedBox.isNewlyAdded ? ' newly-added' : '') +
                                (enhancedBox.isPendingSync ? ' pending-sync' : '')
                            }
                            title={enhancedBox.isPendingSync ? 'Pending sync — will send when online' : undefined}
                            onClick={(e) => {
                                if (box.scanned) return; // scanned boxes are non-interactive
                                if (manualMode) {
                                    handleBoxClick(e, box);
                                }
                                bind.onClick?.(e);
                            }}
                        >
                            {box.scanned && (
                                <div className="scanned-overlay">
                                    <FaCheckCircle className="scanned-check" />
                                    <span className="scanned-label">Scanned</span>
                                </div>
                            )}
                            <div className="barcode">
                                <BarcodeDisplay barcode={box.barcode} boxId={box.id} barcodeComponents={box.barcodeComponents} />
                            </div>
                            {enhancedBox.statusCode && !box.scanned && (
                                <div className={`status-badge ${enhancedBox.statusCode.toLowerCase()}`}>
                                    <span className="status-code">{enhancedBox.statusCode}</span>
                                </div>
                            )}
                            {currentLocation.pathname.indexOf('dispatch/pick') >= 0 && <div className="current-location">{box.warehouseLocation}</div>}
                            {hasActions && (
                                <div className="box-quick-actions">
                                    {canFail && (
                                        <button
                                            className="btn-action-fail"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onFailBox!(enhancedBox);
                                            }}
                                            title="Mark as failed"
                                        >
                                            <FaExclamationTriangle />
                                        </button>
                                    )}
                                    {canRemove && (
                                        <button
                                            className="btn-action-remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onRemoveBox!(enhancedBox);
                                            }}
                                            title="Remove from order"
                                        >
                                            <FaTrash />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Completed boxes section */}
                {completedBoxes.length > 0 && (
                    <div className="completed-section">
                        <div className="section-header">
                            Completed ({completedBoxes.length})
                        </div>
                        {completedBoxes.map((box, index) => {
                            const enhancedBox = box as EnhancedOrderBox;
                            // Use box ID for element id (consistent format)
                            const elementId = box.id ? `box-${box.id}` : box.barcode || `completed-idx-${index}`;
                            return (
                                <div 
                                    key={box.id || box.barcode || `completed-${index}`} 
                                    id={elementId} 
                                    className="box completed"
                                >
                                    <div className="barcode">
                                        <BarcodeDisplay barcode={box.barcode} boxId={box.id} barcodeComponents={box.barcodeComponents} />
                                    </div>
                                    {enhancedBox.statusCode && (
                                        <div className={`status-badge completed ${enhancedBox.statusCode.toLowerCase()}`}>
                                            <span className="status-code">{enhancedBox.statusCode}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </>
    );
};

export default BoxList;